/**
 * prerendering.ts · Warten auf die Aktivierung vorab gerenderter Seiten
 *
 * WARUM ES DAS GIBT: Chrome rendert Seiten unsichtbar vorab, entweder aus eigener
 * Vorhersage (Adressleiste, Suchergebnisse) oder weil wir es per Speculation Rules
 * anstoßen (`SpeculationRules.tsx`: der Schnuppertag-CTA auf /gassi-service). Ein
 * vorab gerendertes Dokument führt sein JavaScript vollständig aus, bevor irgendwer
 * es sieht. Jeder Tracker, der beim Laden „Seite wurde aufgerufen" meldet
 * (GA4 page_view, Meta PageView/ViewContent, page_views/user_sessions, Session-
 * Start in web_sessions, A/B-Exposure, Contentsquare, Clarity), würde damit einen
 * Aufruf zählen, den nie jemand gesehen hat, und beim tatsächlichen Klick keinen
 * zweiten. Wird die Seite nie aktiviert (die Besucherin klickt doch nicht), ist der
 * gezählte Aufruf schlicht erfunden.
 *
 * REGEL: Alles, was einen Seitenaufruf zählt oder ein Analyse-Skript lädt, läuft
 * durch `nachAktivierung` bzw. `bisAktiviert`. Im Normalfall (kein Prerender,
 * Safari, Firefox) ändert sich nichts: der Callback läuft sofort und synchron.
 *
 * Verhalten des Browsers, auf das wir uns stützen:
 *  - `document.prerendering` ist während des Vorab-Renderns true, danach false.
 *  - Beim Sichtbarwerden feuert genau einmal `prerenderingchange` auf document.
 *  - Ein verworfenes Prerender feuert nichts; die Callbacks verfallen mit dem
 *    Dokument. Genau das wollen wir.
 *
 * Siehe docs/superpowers/specs/2026-09-18-landingpages-prerender-scope.md, O4 / 1d.
 */

declare global {
  interface Document {
    /** Chrome ≥ 108. In Safari/Firefox undefined → nie „vorab gerendert“. */
    prerendering?: boolean;
  }
}

/** true, solange dieses Dokument unsichtbar vorab gerendert wird. */
export function istVorabGerendert(): boolean {
  try {
    return typeof document !== "undefined" && document.prerendering === true;
  } catch {
    return false;
  }
}

/**
 * Führt `fn` sofort aus, wenn die Seite nicht vorab gerendert wird, sonst genau
 * einmal beim `prerenderingchange`. Gibt eine Abbruch-Funktion zurück (für
 * useEffect-Cleanups); nach dem Abbruch läuft `fn` nicht mehr.
 *
 * `fn` darf nicht werfen, die Aufrufer sind Tracker mit eigener try/catch-Hülle.
 */
export function nachAktivierung(fn: () => void): () => void {
  if (!istVorabGerendert()) {
    fn();
    return () => {};
  }

  let aktiv = true;
  const handler = () => {
    document.removeEventListener("prerenderingchange", handler);
    if (aktiv) fn();
  };
  document.addEventListener("prerenderingchange", handler, { once: true });

  return () => {
    aktiv = false;
    document.removeEventListener("prerenderingchange", handler);
  };
}

/** Promise-Form von `nachAktivierung` für async-Abläufe (`await bisAktiviert()`). */
export function bisAktiviert(): Promise<void> {
  if (!istVorabGerendert()) return Promise.resolve();
  return new Promise((resolve) => {
    nachAktivierung(resolve);
  });
}

/**
 * Kann dieser Browser Speculation Rules (Chrome/Edge ≥ 105 für prefetch,
 * ≥ 109 für prerender)? Safari/Firefox: false. Wer eine Regel einhängt oder
 * einen Link für die Aktivierung als echte Navigation rendern will, fragt hier.
 */
export function unterstuetztSpeculationRules(): boolean {
  try {
    const supports = (
      HTMLScriptElement as unknown as { supports?: (type: string) => boolean }
    ).supports;
    return typeof supports === "function" && supports.call(HTMLScriptElement, "speculationrules");
  } catch {
    return false;
  }
}
